<template>
  <CheckoutStep>
    <template #header>
      <Stepper :has-bouygues-steps="hasSelectedMobilePlan(items)" />
    </template>
    <template #aside>
      <CheckoutSummary
        :discount
        :discount-token
        :items="getAvailableItems(items)"
        :selected-payment-method="paymentMethod"
        :summary
        :swapOffer
        with-extended-legal
      />
    </template>

    <div aria-live="polite" role="status">
      <div class="flex flex-col gap-48">
        <UnavailableCountry
          :country="addressList.deliver?.country || null"
          :shippable-countries
        />

        <div ref="shippingAddress">
          <ShippingAddress
            ref="shippingAddressComponent"
            :address="addressList.deliver"
            :collection-point="addressList.deliverCollectionPoint"
            :shippable-countries
            @country-change="handleShippingCountryChange"
            @updated="onUpdated"
            @updating="onUpdating"
          />

          <SameAsShippingCheckbox
            v-model="isSameAddressesCheckboxSelected"
            :addressList
            class="mt-12"
          />
        </div>

        <div v-show="!isSameAddressesCheckboxSelected" ref="billingAddress">
          <BillingAddress
            ref="billingAddressComponent"
            :address="addressList.bill"
            :billable-countries
            @country-change="handleBillingCountryChange"
            @updated="onUpdated"
            @updating="onUpdating"
          />
        </div>

        <UserInformations
          v-if="
            userInformationStore.isFormRequired ||
            isNationalIdShownForBilling(selectedCountry as Country)
          "
          :country="selectedCountry"
          :national-id="addressList.bill?.customerIdNumber ?? ''"
          @submit="onUserInformationsSubmit"
          @submit-failed="onUserInformationsSubmitFailed"
        />

        <Delivery
          :items
          :last-collection-point
          :show-error="hasCollectionPointError"
          @updated="onUpdated"
          @updating="onUpdating"
        />

        <div class="flex justify-end">
          <!-- We are hardcoding the disabled styles since we need to be able to click the element -->
          <RevButton
            ref="submit"
            :aria-disabled="isButtonDisabled"
            :class="{
              'bg-action-default-hi-disabled text-onaction-default-hi-disabled cursor-not-allowed hover:bg-action-default-hi-disabled':
                isButtonDisabled,
            }"
            data-qa="submit-button"
            full-width="adaptive"
            :loading="loading || isLoading"
            type="submit"
            variant="primary"
            @click="onSubmit"
          >
            {{ i18n(translations.submitButton) }}
          </RevButton>
        </div>

        <GDPRNotice class="!p-0" page="delivery" />
        <ReassuranceItems
          :selected-mobile-plan="getSelectedMobilePlan(items)"
        />
      </div>
    </div>
  </CheckoutStep>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef } from 'vue'

import type { BuybackOffer, Country } from '@backmarket/http-api'
import type { PaymentMethod } from '@backmarket/http-api/src/api-specs-payment/payment/payment.types'
import { useBuybackOffer } from '@backmarket/nuxt-layer-buyback/composables/buybackOffer/useBuybackOffer'
import CheckoutStep from '@backmarket/nuxt-layer-checkout/components/CheckoutStep/CheckoutStep.vue'
import { useUserInformationStore } from '@backmarket/nuxt-layer-checkout/stores/userInformationStore'
import { navigateToPaymentPage } from '@backmarket/nuxt-layer-payment/pages/navigateToPaymentPage'
import { isNationalIdShownForBilling } from '@backmarket/nuxt-module-address/utils/nationalId/isNationalIdShownForBilling'
import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { decamelizeKeys } from '@backmarket/utils/object/decamelizeKeys'
import { pick } from '@backmarket/utils/object/pick'
import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'
import { RevButton } from '@ds/components/Button'

import CheckoutSummary from '~/scopes/checkout/components/CheckoutSummary/CheckoutSummary.vue'
import Delivery from '~/scopes/checkout/components/Delivery/Delivery.vue'
import GDPRNotice from '~/scopes/checkout/components/GDPRNotice/GDPRNotice.vue'
import ReassuranceItems from '~/scopes/checkout/components/ReassuranceItems/ReassuranceItems.vue'
import UnavailableCountry from '~/scopes/checkout/components/ShippingAddress/components/UnavailableCountry/UnavailableCountry.vue'
import { useRedirectIfCartEmpty } from '~/scopes/checkout/composables/redirects/useRedirectIfCartEmpty'
import { useCheckoutTracking } from '~/scopes/checkout/composables/useCheckoutTracking'
import { useSaveBouyguesAddress } from '~/scopes/checkout/composables/useSaveBouyguesAddress'
import { CHECKOUT } from '~/scopes/checkout/routes-names'
import type { Discount } from '~/scopes/checkout/types/discount'
import {
  isBillingAddressComplete,
  isShippingAddressComplete,
} from '~/scopes/checkout/utils/address'
import { getAvailableItems } from '~/scopes/checkout/utils/cart'
import { getSelectedMobilePlan } from '~/scopes/checkout/utils/getSelectedMobilePlan'
import { hasCollectionPointAddressCompleted } from '~/scopes/checkout/utils/hasCollectionPointAddressCompleted'
import { hasCollectionPointOptionSelected } from '~/scopes/checkout/utils/hasCollectionPointOptionSelected'
import { hasSelectedMobilePlan } from '~/scopes/checkout/utils/hasSelectedMobilePlan'
import { isCountryShippable } from '~/scopes/checkout/utils/isCountryShippable'

import Stepper from '../../components/Stepper/Stepper.vue'

import translations from './AddressesStep.translations'
import BillingAddress from './components/BillingAddress/BillingAddress.vue'
import SameAsShippingCheckbox from './components/SameAsShippingCheckbox/SameAsShippingCheckbox.vue'
import ShippingAddress from './components/ShippingAddress/ShippingAddress.vue'
import UserInformations from './components/UserInformations/UserInformations.vue'
import { useUserInformationsValidation } from './composables/useUserInformationsValidation'

const props = defineProps<{
  loading: boolean
  items: ApiSpecs['/bm/cart']['GET']['response']['cartItems']
  addressList: ApiSpecs['/bm/cart']['GET']['response']['addressList']
  billableCountries: ApiSpecs['/bm/cart']['GET']['response']['billableCountries']
  shippableCountries: ApiSpecs['/bm/cart']['GET']['response']['shippableCountries']
  lastCollectionPoint: ApiSpecs['/bm/cart']['GET']['response']['lastCollectionPoint']
  summary: ApiSpecs['/bm/cart']['GET']['response']['summary']
  swapOffer: BuybackOffer | null
  discount: Discount
  discountToken: string
  paymentMethod: PaymentMethod | null
}>()

const emit = defineEmits<{
  (e: 'update', callback?: () => void): void
}>()

const i18n = useI18n()
const logger = useLogger()
const $httpFetch = useHttpFetch()

const { trackClick } = useTracking()
const { trackFunnel } = useCheckoutTracking()
const { redirectIfCartEmpty } = useRedirectIfCartEmpty('ADDRESSES')
const { resetBuybackOffer } = useBuybackOffer()
const { saveBouyguesAddress } = useSaveBouyguesAddress()
const { openErrorToast } = useTheToast()
const userInformationStore = useUserInformationStore()

const {
  userInformationsValues,
  onUserInformationsSubmit,
  onUserInformationsSubmitFailed,
  validateUserInformations,
} = useUserInformationsValidation()

const isSameAddressesCheckboxSelected = ref(false)
const isLoading = ref(false)
const hasCollectionPointError = ref(false)

const selectedBillingCountry = ref<string>(
  props.addressList.bill?.country ?? '',
)

function handleBillingCountryChange(country?: string) {
  selectedBillingCountry.value = country ?? ''
}

const selectedShippingCountry = ref<string>(
  props.addressList.deliver?.country ?? '',
)

function handleShippingCountryChange(country?: string) {
  selectedShippingCountry.value = country ?? ''
}

const selectedCountry = computed(() => {
  if (isSameAddressesCheckboxSelected.value) {
    return selectedShippingCountry.value
  }

  return selectedBillingCountry.value
})

const shippingAddress = useTemplateRef<HTMLElement>('shippingAddress')
const billingAddress = useTemplateRef<HTMLElement>('billingAddress')
const shippingAddressComponent = useTemplateRef<
  InstanceType<typeof ShippingAddress>
>('shippingAddressComponent')
const billingAddressComponent = useTemplateRef<
  InstanceType<typeof BillingAddress>
>('billingAddressComponent')

const formInEditMode = computed(() => {
  if (shippingAddressComponent.value?.isInEditMode()) {
    return shippingAddress.value
  }

  if (
    !isSameAddressesCheckboxSelected.value &&
    billingAddressComponent.value?.isInEditMode()
  ) {
    return billingAddress.value
  }

  return false
})

const isButtonDisabled = computed(() => {
  return (
    props.loading ||
    isLoading.value ||
    !!formInEditMode.value ||
    !isCountryShippable(
      props.addressList.deliver?.country,
      props.shippableCountries,
    )
  )
})

onMounted(async () => {
  await redirectIfCartEmpty(props.items, CHECKOUT.CART)

  trackFunnel(props.items, props.swapOffer)
})

async function saveBillingAddress() {
  if (!isSameAddressesCheckboxSelected.value || !props.addressList.deliver) {
    return true
  }

  try {
    await $httpFetch('/bm/client/addresses/v2/billing', {
      method: 'POST',
      body: removeEmptyValuesInObject(
        pick(props.addressList.deliver, [
          'city',
          'company',
          'country',
          'firstName',
          'lastName',
          'postalCode',
          'stateOrProvince',
          'street',
          'street2',
        ]),
      ) as ApiSpecs['/bm/client/addresses/v2/billing']['POST']['body'],
    })

    return true
  } catch {
    throw new Error()
  }
}

async function saveUserInformations() {
  if (!userInformationStore.isFormRequired) {
    return true
  }

  try {
    await $httpFetch('/bm/cart/update-user-information', {
      method: 'POST',
      body: removeEmptyValuesInObject({
        formType: 'INSURANCE_ONLY_FORM',
        birthdate: userInformationsValues.value.birthdate,
        taxId: userInformationsValues.value.nationalId,
      }) as ApiSpecs['/bm/cart/update-user-information']['POST']['body'],
    })

    return true
  } catch {
    throw new Error()
  }
}

async function saveBuybackAddress() {
  if (!props.swapOffer || !props.addressList.deliver) {
    return true
  }

  try {
    await $httpFetch('/bm/buyback/v1/customer/address', {
      method: 'POST',
      body: decamelizeKeys(
        removeEmptyValuesInObject(
          pick(props.addressList.deliver, [
            'city',
            'company',
            'country',
            'firstName',
            'lastName',
            'postalCode',
            'stateOrProvince',
            'street',
            'street2',
            'phone',
          ]),
        ),
      ) as ApiSpecs['/bm/buyback/v1/customer/address']['POST']['body'],
    })

    return true
  } catch (err) {
    const error = err as Error
    logger.error('[BUYBACK][SWAP] Error while submitting customer address', {
      error,
      owners: ['bot-squad-checkout-front'],
    })

    await $httpFetch('/bm/cart/swap', { method: 'DELETE' })
    resetBuybackOffer()

    return false
  }
}

async function saveBouyguesAddresses() {
  if (
    !hasSelectedMobilePlan(props.items) ||
    !props.addressList.deliver ||
    !props.addressList.bill
  ) {
    return true
  }

  try {
    await Promise.all([
      saveBouyguesAddress(props.addressList.deliver, 'SHIPPING'),
      saveBouyguesAddress(props.addressList.bill, 'BILLING'),
    ])

    return true
  } catch (err) {
    // TODO handle validation errors
    const error = err as Error
    logger.error('[BOUYGUES] Error while submitting customer address', {
      error,
      owners: ['bot-squad-checkout-front'],
    })

    throw new Error()
  }
}

async function validateCollectionPoint() {
  hasCollectionPointError.value = false

  if (
    hasCollectionPointOptionSelected(props.items) &&
    !hasCollectionPointAddressCompleted(props.lastCollectionPoint)
  ) {
    hasCollectionPointError.value = true

    const listing = props.items.find((item) =>
      item.options.some((option) =>
        option.choices.some(
          (choice) => choice.isCollectionPoint && choice.selected,
        ),
      ),
    )

    const shipping = (listing?.options[0].choices || []).find(
      (choice) => choice.isCollectionPoint,
    )

    const element = document.getElementById(
      `cart-shipping-option-${listing?.listingId}-${shipping?.shippingId}`,
    )
    element?.scrollIntoView({ block: 'center', behavior: 'smooth' })

    throw new Error()
  }
}

function showUnsavedChangesError(element?: HTMLElement) {
  openErrorToast({
    title: i18n(translations.nextErrorToastTitle),
    content: i18n(translations.nextErrorToastDescription),
  })

  element?.scrollIntoView({ behavior: 'smooth' })
}

async function onSubmit() {
  if (formInEditMode.value) {
    showUnsavedChangesError(formInEditMode.value)

    return
  }

  if (isButtonDisabled.value) return

  isLoading.value = true

  try {
    trackClick({
      name: 'go_payment',
      zone: 'address',
    })

    if (!(await validateUserInformations())) {
      isLoading.value = false

      return
    }

    await Promise.all([
      validateCollectionPoint(),
      saveBillingAddress(),
      saveBuybackAddress(),
      saveBouyguesAddresses(),
      saveUserInformations(),
    ])

    // We need to fetch the cart again to make sure billing address & everything is up-to-date
    // Especially when "same billing address" is checked
    emit('update', () => {
      if (!isShippingAddressComplete(props.addressList.deliver)) {
        shippingAddress.value?.scrollIntoView({ behavior: 'smooth' })

        return
      }
      if (
        !isBillingAddressComplete(props.addressList.bill) &&
        !isSameAddressesCheckboxSelected.value
      ) {
        billingAddress.value?.scrollIntoView({ behavior: 'smooth' })

        return
      }

      navigateToPaymentPage({ name: CHECKOUT.PAYMENT })
      isLoading.value = false
    })
  } catch (e) {
    // TODO Show errors

    isLoading.value = false
  }
}

function onUpdating() {
  hasCollectionPointError.value = false
  isLoading.value = true
}

function onUpdated(cb?: () => void) {
  isLoading.value = false
  emit('update', cb)
}
</script>
